<template>
    <card-normal without-padding :with-overflow="false">
        <div class="flow-card">
            <div class="flow-card__content">
                <div class="flow-card__header">
                    <div class="flow-card__icon">
                        <svg-icon
                            :icon="flowIcon(props.flow.triggers[0])"
                            :size="IconSize.md"
                            is-custom-icon
                        />
                    </div>

                    <chip-informative v-if="props.flow.active" :color="'green-500'">
                        Actief
                    </chip-informative>
                    <chip-informative v-else :color="'gray-500'">
                        Inactief
                    </chip-informative>

                    <collapse-box-decorator
                        horizontal-position="right"
                    >
                        <template #default>
                            <svg-icon
                                :icon="IconEllipsisVertical"
                                is-custom-icon
                            />
                        </template>
                        <template #content>
                            <collapse-option-container>
                                <collapse-option v-if="flow.active" @click="deactivateFlow">
                                    Deactiveren
                                </collapse-option>
                                <collapse-option v-else @click="activateFlow">
                                    Activeren
                                </collapse-option>
                                <collapse-option @click="editFlow">
                                    Bewerken
                                </collapse-option>
                                <collapse-option @click="$router.push({ name: 'flow-delete', params: { flowId: flow.id.value } })">
                                    <text-decorator :color="'red-500'">
                                        Verwijderen
                                    </text-decorator>
                                </collapse-option>
                            </collapse-option-container>
                        </template>
                    </collapse-box-decorator>
                </div>

                <text-decorator class="flow-card__title">
                    {{ props.flow.name }}
                </text-decorator>

                <text-decorator class="flow-card__description">
                    {{ flowDescription(props.flow.triggers[0]) }}
                </text-decorator>

                <text-decorator>
                    {{ props.flow.brand ? props.flow.brand.name : '-' }}
                    &bullet;
                    <template v-if="props.flow.teams.length > 0">
                        {{ props.flow.teams[0].name }}
                        <template v-if="props.flow.teams.length > 1">
                            (+{{ props.flow.teams.length - 1 }})
                        </template>
                    </template>
                    <template v-else>
                        -
                    </template>
                </text-decorator>
            </div>

            <spinner-loader v-if="loading" />
            <div v-else-if="displayData" class="flow-card__data">
                <carousel-container radius>
                    <template #slides>
                        <carousel-slide
                            v-for="(statistic, key) in displayData"
                            :key="key"
                            background-color="'primary'"
                            color="white"
                        >
                            <statistics-entry
                                variant="flow-card"
                                :title="statistic.title"
                                :current-value="statistic.currentValue"
                                :previous-value="statistic.previousValue"
                                :render-type="statistic.renderType"
                            >
                                <template #difference-text>
                                    {{ props.range.comparisonDescription }}
                                </template>
                            </statistics-entry>

                            <linear-graph
                                v-if="statistic.graph"
                                :data-scale="statistic.graph.dataScale"
                                :data="statistic.graph.data"
                                :scaled-data="statistic.graph.scaledData"
                                :background-color="statistic.graph.backgroundColor"
                                :stroke="statistic.graph.stroke"
                                :fill="statistic.graph.fill"
                                :padding-top="120"
                                :padding-bottom="60"
                            />
                        </carousel-slide>
                    </template>

                    <template #interaction>
                        <carousel-navigation-arrow
                            direction="left"
                            horizontal-position="left"
                            inverted
                        />

                        <carousel-navigation-arrow
                            direction="right"
                            horizontal-position="right"
                            inverted
                        />
                        <carousel-navigation-dots
                            horizontal-position="center"
                            vertical-position="bottom"
                            inverted
                        />
                    </template>
                </carousel-container>
            </div>
        </div>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        CarouselContainer,
        CarouselSlide,
        CarouselNavigationArrow,
        CarouselNavigationDots,
        ChipInformative,
        TextDecorator,
        SvgIcon,
        CardNormal,
        CollapseBoxDecorator,
        CollapseOptionContainer,
        CollapseOption,
        IconSize,
    } from '@keyvue/one-ui';

    import {
        ActivateFlowCommand,
        DeactivateFlowCommand,
        type Flow,
        GetStatisticsForFlowCommand,
        StatisticsForFlow,
        Range,
    } from '@keyvue/one-api-client';

    import { flowDescription, flowIcon } from '@/flow/flow/utils/flow-types';
    import IconEllipsisVertical from '@keyvue/one-awesome-icons/icons/regular/ellipsis-vertical.svg?component';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import { useRouter } from 'vue-router';
    import LinearGraph from '@/flow/flow/components/LinearGraph.vue';
    import { computed, onMounted, ref, watch } from 'vue';
    import StatisticsEntry from '@/flow/statistics/components/StatisticsEntry.vue';
    import generateDisplayData from '@/flow/statistics/utils/generateDisplayData';

    interface Props {
        flow: Flow
        range: Range
    }

    const props = defineProps<Props>();
    const emit = defineEmits<{
        (e: 'change'): void;
    }>();

    const router = useRouter();
    const { client } = useClient();
    const { notifySaved, notifyError } = useNotify();

    const loading = ref(false);
    const statistics = ref<StatisticsForFlow>();

    const displayData = computed(() => {
        if (!statistics.value) {
            return [];
        }

        return generateDisplayData(statistics.value);
    });

    async function updateStatistics() {
        loading.value = true;
        statistics.value = await client.dispatch(new GetStatisticsForFlowCommand(props.flow.id, props.range));
        loading.value = false;
    }

    async function activateFlow() {
        try {
            await client.dispatch(ActivateFlowCommand.fromFlow(props.flow));
            notifySaved(undefined, 'De flow is geactiveerd');
            emit('change');
        } catch (error) {
            notifyError(error);
        }
    }

    async function deactivateFlow() {
        try {
            await client.dispatch(DeactivateFlowCommand.fromFlow(props.flow));
            notifySaved(undefined, 'De flow is gedeactiveerd');
            emit('change');
        } catch (error) {
            notifyError(error);
        }
    }

    async function editFlow() {
        await router.push({
            name: 'edit-flow',
            params: {
                flowId: props.flow.id.value,
            },
        });
    }

    onMounted(() => {
        updateStatistics();
    });

    watch(() => props.range, () => {
        updateStatistics();
    });
</script>

<style scoped lang="scss">
.flow-card {
    min-height: 300px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    border-radius: size(sm);

    &__content {
        padding: size(md);
        display: grid;
        gap: size(sm);
        grid-template-rows: min-content min-content 1fr min-content;
        justify-content: space-between;
    }

    &__data {
        border-top-right-radius: size(sm);
        border-bottom-right-radius: size(sm);
    }

    &__icon {
        background-color: color(gray-100);
        border-radius: 50%;
        width: 64px; /* icon-size(md) + 2x size(md) (width + 2x padding) */

        :deep(.svg-icon) {
            padding: size(md);
        }
    }

    &__header {
        display: grid;
        grid-template-columns: auto min-content min-content;
        gap: size(sm);
    }

    &__title {
        font-weight: 900;
    }
}
</style>
